import { MeUpdate } from "@divespots/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { KeyRound, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";
import { ME_KEY, useSession, useSessionActions } from "../admin/session.ts";
import { ConfirmSheet, Field, Section } from "../admin/ui.tsx";
import { api } from "../api/client.ts";
import { isApiRequestError } from "../api/errors.ts";
import { addPasskey, deletePasskey, listPasskeys, passkeysSupported } from "../auth/diver-auth.ts";
import { dismissOffer, offerDismissed } from "../auth/passkey-offer.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { useDecisionMails } from "./useDecisionMails.ts";
import { ChangePasswordForm } from "./auth/ChangePasswordForm.tsx";
import { FavouritesSection } from "./FavouritesSection.tsx";

/** `/:lang/konto`: name, password, passkeys, sign out, delete the account. */
export function AccountPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const { user, staff } = useSession();
  const actions = useSessionActions();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [name, setName] = useState(user?.name ?? "");
  const [nameError, setNameError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [passkeyNote, setPasskeyNote] = useState<string | null>(null);
  const [offerHidden, setOfferHidden] = useState(() => (user ? offerDismissed(user.id) : true));

  const mails = useDecisionMails(!staff);
  const location = useLocation();
  // the decision mails' footer links to /konto#benachrichtigungen: scroll there once, and put the
  // keyboard on the checkbox as soon as it is usable (a disabled input cannot take focus)
  const anchored = useRef({ scrolled: false, focused: false });
  const checkbox = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (location.hash !== "#benachrichtigungen" || !user || staff) return;
    if (!anchored.current.scrolled) {
      anchored.current.scrolled = true;
      document.getElementById("benachrichtigungen")?.scrollIntoView?.();
    }
    if (!anchored.current.focused && mails.on !== undefined) {
      anchored.current.focused = true;
      checkbox.current?.focus({ preventScroll: true });
    }
  }, [location.hash, user, staff, mails.on]);

  const passkeys = useQuery({
    queryKey: ["passkeys"],
    queryFn: () => listPasskeys(),
    enabled: passkeysSupported(),
    retry: false,
  });

  const rename = useMutation({
    mutationFn: (n: string) => api.updateMe({ name: n }),
    onSuccess: async (me) => {
      qc.setQueryData(ME_KEY, me.user);
      setSaved(true);
    },
    onError: () => setNameError(t("admin.err.save")),
  });

  const addKey = useMutation({
    mutationFn: () =>
      addPasskey(`${user?.name ?? "divespots"} · ${new Date().toLocaleDateString(locale)}`),
    onSuccess: async (res) => {
      setPasskeyNote(
        res.ok
          ? t("diver.account.passkeyAdded")
          : res.cancelled
            ? null
            : t("diver.account.passkeyFailed"),
      );
      await passkeys.refetch();
    },
    onError: () => setPasskeyNote(t("diver.account.passkeyFailed")),
  });

  const removeKey = useMutation({
    mutationFn: (id: string) => deletePasskey(id),
    onSuccess: () => void passkeys.refetch(),
  });

  const del = useMutation({
    mutationFn: () => api.deleteMe(),
    onSuccess: async () => {
      await actions.signOutAndClear().catch(() => undefined);
      void navigate(`/${locale}/`, { replace: true });
    },
  });

  if (!user) return null;

  const saveName = () => {
    setSaved(false);
    setNameError(null);
    const parsed = MeUpdate.safeParse({ name });
    if (!parsed.success) return setNameError(t("diver.account.nameInvalid"));
    rename.mutate(parsed.data.name);
  };

  // offered once, and only to someone who has no passkey yet
  const welcome =
    params.get("welcome") === "1" &&
    !offerHidden &&
    passkeysSupported() &&
    passkeys.data !== undefined &&
    passkeys.data.length === 0;
  const hideOffer = () => {
    dismissOffer(user.id);
    setOfferHidden(true);
    setParams({}, { replace: true });
  };

  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("diver.account.title")}</h1>

      {welcome ? (
        <section className="adm-warning" aria-labelledby="passkey-offer">
          <KeyRound aria-hidden="true" size={20} strokeWidth={1.75} />
          <div className="grid gap-2">
            <h2 id="passkey-offer" className="ds-text-headline m-0">
              {t("diver.account.offerTitle")}
            </h2>
            <p className="ds-text-subheadline m-0">{t("diver.account.offerText")}</p>
            <div className="adm-actions">
              <Button
                variant="filled"
                disabled={addKey.isPending}
                onClick={() => {
                  addKey.mutate();
                  hideOffer();
                }}
              >
                {t("diver.account.passkeySetup")}
              </Button>
              <Button variant="tinted" onClick={hideOffer}>
                {t("diver.account.later")}
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <Section title={t("diver.account.profile")}>
        <p className="ds-text-subheadline text-label-secondary m-0">{user.email}</p>
        <form
          className="grid gap-3"
          noValidate
          aria-label={t("diver.account.profile")}
          onSubmit={(e) => {
            e.preventDefault();
            saveName();
          }}
        >
          <Field
            label={t("diver.account.name")}
            error={nameError ?? undefined}
            hint={t("diver.account.nameHint")}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                className="adm-input"
                maxLength={120}
                value={name}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                onChange={(e) => {
                  setName(e.target.value);
                  setSaved(false);
                }}
              />
            )}
          </Field>
          <div className="adm-actions">
            <Button type="submit" variant="filled" disabled={rename.isPending}>
              {t("admin.common.save")}
            </Button>
            {saved ? (
              <p className="adm-ok m-0" role="status">
                {t("admin.common.saved")}
              </p>
            ) : null}
          </div>
        </form>
      </Section>

      <FavouritesSection />

      <Section title={t("diver.account.passkeys")}>
        {!passkeysSupported() ? (
          <p className="ds-text-subheadline text-label-secondary m-0">
            {t("diver.account.passkeyUnsupported")}
          </p>
        ) : (
          <>
            <p className="ds-text-subheadline text-label-secondary m-0">
              {t("diver.account.passkeyLead")}
            </p>
            {(passkeys.data ?? []).length > 0 ? (
              <ul className="ds-list adm-list" aria-label={t("diver.account.passkeys")}>
                {(passkeys.data ?? []).map((p) => (
                  <li key={p.id} className="ds-row">
                    <span className="ds-row__title">
                      <span className="ds-text-headline">
                        {p.name ?? t("diver.account.passkeyUnnamed")}
                      </span>
                      {p.createdAt ? (
                        <span className="ds-text-subheadline text-label-secondary block">
                          {formatTimestamp(p.createdAt, locale)}
                        </span>
                      ) : null}
                    </span>
                    <Button
                      variant="destructive"
                      aria-label={t("diver.account.passkeyRemove", { name: p.name ?? "" })}
                      icon={<Trash2 aria-hidden="true" strokeWidth={1.75} />}
                      disabled={removeKey.isPending}
                      onClick={() => removeKey.mutate(p.id)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="ds-text-subheadline m-0">{t("diver.account.passkeyNone")}</p>
            )}
            {passkeyNote ? (
              <p className="ds-text-footnote m-0" role="status">
                {passkeyNote}
              </p>
            ) : null}
            <div className="adm-actions">
              <Button
                variant="tinted"
                icon={<KeyRound aria-hidden="true" strokeWidth={1.75} />}
                disabled={addKey.isPending}
                onClick={() => addKey.mutate()}
              >
                {t("diver.account.passkeySetup")}
              </Button>
            </div>
          </>
        )}
      </Section>

      {staff ? null : (
        <div id="benachrichtigungen">
          <Section title={t("diver.account.notifications")}>
            <label className="adm-check">
              <input
                type="checkbox"
                ref={checkbox}
                checked={mails.on === true}
                disabled={mails.on === undefined}
                aria-describedby="decision-mails-hint"
                // kept enabled while a save runs (a disabled input drops keyboard focus); a change meanwhile is ignored
                onChange={(e) => {
                  if (!mails.saving) mails.set(e.target.checked);
                }}
              />
              <span className="ds-text-body">{t("diver.account.decisionMails")}</span>
            </label>
            <p id="decision-mails-hint" className="ds-text-footnote text-label-secondary m-0">
              {t("diver.account.decisionMailsHint")}
            </p>
            {mails.saved ? (
              <p className="adm-ok m-0" role="status">
                {t("admin.common.saved")}
              </p>
            ) : null}
            {mails.loadFailed ? (
              <div className="adm-actions">
                <Button variant="tinted" onClick={mails.retry}>
                  {t("common.retry")}
                </Button>
              </div>
            ) : null}
            {mails.saveFailed || mails.loadFailed ? (
              <p className="adm-error m-0" role="alert">
                {mails.rateLimited
                  ? t("diver.auth.err.rateLimited")
                  : mails.saveFailed
                    ? t("diver.account.decisionMailsSaveFailed")
                    : t("diver.account.decisionMailsLoadFailed")}
              </p>
            ) : null}
          </Section>
        </div>
      )}

      <Section title={t("diver.password.title")}>
        <ChangePasswordForm />
      </Section>

      <Section title={t("diver.account.session")}>
        <div className="adm-actions">
          <Link className="ds-btn ds-btn--tinted" to={`/${locale}/konto/vorschlaege`}>
            {t("diver.nav_suggestions")}
          </Link>
          <Button
            variant="tinted"
            onClick={async () => {
              await actions.signOutAndClear();
              void navigate(`/${locale}/`);
            }}
          >
            {t("diver.signOut")}
          </Button>
        </div>
      </Section>

      <Section title={t("diver.account.dangerTitle")}>
        <p className="ds-text-subheadline text-label-secondary m-0">
          {t("diver.account.dangerText")}
        </p>
        <div className="adm-actions">
          <Button variant="destructive" onClick={() => setConfirmDelete(true)}>
            {t("diver.account.delete")}
          </Button>
        </div>
      </Section>

      {confirmDelete ? (
        <ConfirmSheet
          title={t("diver.account.deleteTitle")}
          body={t("diver.account.deleteBody")}
          confirmLabel={t("diver.account.deleteConfirm")}
          busy={del.isPending}
          error={
            del.isError
              ? isApiRequestError(del.error) && del.error.code === "conflict"
                ? t("diver.account.deleteStaff")
                : t("admin.err.save")
              : undefined
          }
          onConfirm={() => del.mutate()}
          onCancel={() => setConfirmDelete(false)}
        />
      ) : null}
    </div>
  );
}
